import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from 'material-ui/styles';
import Typography from 'material-ui/Typography';
import TextField from 'material-ui/TextField';
import MuiButton from 'material-ui/Button';
import Wrapper from '../common/Wrapper';
import Header from '../common/Header';
import NotificationBanner from '../common/NotificationBanner';
import NotificationBar from '../Parts/NotificationBar';
import Button from '../Parts/Button';
import ArrowToolTip from '../common/ArrowToolTip';
import DropdownMenu from '../common/DropdownMenu';
import SettingIcon from '../Icons/SettingIcon';
import SearchIcon from '../../assets/icons/search.svg';
import UploadCsvDialog from './dialogs/UploadCsvDialog';

const POLLING_INTERVAL_MS = 2500;
const MAX_POLLING_COUNT = 480;
const PENDING_JOBS_STORAGE_KEY = 'corporate_number_assign_pending_jobs';
const PERCENT_MAX = 100;

const styles = () => ({
  root: {
    width: '100%',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'nowrap',
  },

  main: {
    background: '#ECEEF0',
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
  },

  container: {
    margin: '100px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    textAlign: 'center',
    width: '100%',
  },

  title: {
    fontSize: '18px',
    lineHeight: '22px',
  },

  subheading: {
    marginTop: '14px',
    lineHeight: '24px',
    fontSize: '14px',
  },

  resultContainer: {
    width: 'calc(100% - 120px)',
    maxWidth: '1280px',
    margin: '0 60px 60px',
    background: '#FFFFFF',
    border: '1px solid #D8DBDF',
    boxSizing: 'border-box',
    padding: '20px',
    textAlign: 'left',
  },

  resultTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
    marginBottom: '14px',
  },

  summaryAndFilterRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    marginBottom: '12px',
    flexWrap: 'wrap',
  },

  summary: {
    display: 'flex',
    gap: '20px',
    marginBottom: '0',
    flexWrap: 'wrap',
  },

  summaryItem: {
    fontSize: '13px',
    color: '#181819',
  },

  resultFilterRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginBottom: '0',
  },

  searchGroup: {
    display: 'flex',
    alignItems: 'center',
    width: '414px',
    maxWidth: '414px',
  },

  searchField: {
    flex: 1,
    maxWidth: 'none',
    marginRight: '8px',
    '& input': {
      border: '1px solid #D8DBDF',
      padding: '10px 12px',
      fontSize: '13px',
      borderRadius: '2px',
      height: '18px',
      boxSizing: 'content-box',
    },
  },

  iconButton: {
    color: '#71737B',
    padding: 0,
    width: '32px',
    minWidth: '32px',
    height: '32px',
    minHeight: '32px',
  },

  searchIconButton: {
    color: '#71737B',
    padding: 0,
    width: '32px',
    minWidth: '32px',
    height: '32px',
    minHeight: '32px',
    marginRight: '10px',
  },

  noResultText: {
    padding: '12px 0',
    fontSize: '13px',
    color: '#71737B',
  },

  tableWrap: {
    overflowX: 'auto',
    border: '1px solid #D8DBDF',
  },

  table: {
    width: '100%',
    borderCollapse: 'collapse',
    tableLayout: 'fixed',
    background: '#FFFFFF',
  },

  th: {
    background: '#F4F6F9',
    borderBottom: '1px solid #D8DBDF',
    borderRight: '1px solid #D8DBDF',
    fontSize: '12px',
    fontWeight: 'bold',
    padding: '10px',
    whiteSpace: 'nowrap',
  },

  td: {
    borderBottom: '1px solid #E7E9EB',
    borderRight: '1px solid #E7E9EB',
    fontSize: '12px',
    padding: '10px',
    wordBreak: 'break-word',
  },

});

class CorporateNumberAssign extends React.Component {
  state = {
    showUploadDialog: false,
    isUploading: false,
    uploadStatus: 'queued',
    progressPercent: null,
    resultSummary: null,
    resultRows: [],
    searchInputValue: '',
    searchKeyword: '',
    confidenceFilter: 'all',
    showNotification: false,
    notificationMessage: '',
    notificationType: 'success',
  };

  pollingCount = 0;
  pollingTimer = null;

  componentDidMount() {
    this.props.getUserInfo();
    this.resumePendingJob();
  }

  componentWillUnmount() {
    this.clearPollingTimer();
  }

  toggleUploadDialog = () => {
    this.setState({ showUploadDialog: !this.state.showUploadDialog });
  };

  displayNotification = (message, type = 'error') => {
    this.setState({
      showNotification: true,
      notificationMessage: message,
      notificationType: type,
    });
  };

  clearPollingTimer = () => {
    if (this.pollingTimer) {
      clearTimeout(this.pollingTimer);
      this.pollingTimer = null;
    }
  };

  getPendingJobsFromStorage = () => {
    if (typeof window === 'undefined' || !window.localStorage) {
      return [];
    }

    const raw = window.localStorage.getItem(PENDING_JOBS_STORAGE_KEY);
    if (!raw) {
      return [];
    }

    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  };

  savePendingJobsToStorage = (jobs) => {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    window.localStorage.setItem(PENDING_JOBS_STORAGE_KEY, JSON.stringify(jobs));
  };

  upsertPendingJob = (job) => {
    if (!job || !job.jobId) {
      return;
    }

    const jobs = this.getPendingJobsFromStorage();
    const nextJob = {
      ...job,
      updatedAt: new Date().toISOString(),
    };
    const targetIndex = jobs.findIndex((item) => item.jobId === nextJob.jobId);

    if (targetIndex >= 0) {
      jobs[targetIndex] = {
        ...jobs[targetIndex],
        ...nextJob,
      };
    } else {
      jobs.push({
        ...nextJob,
        createdAt: nextJob.createdAt || new Date().toISOString(),
      });
    }

    this.savePendingJobsToStorage(jobs);
  };

  removePendingJob = (jobId) => {
    if (!jobId) {
      return;
    }

    const jobs = this.getPendingJobsFromStorage();
    const nextJobs = jobs.filter((job) => job.jobId !== jobId);
    this.savePendingJobsToStorage(nextJobs);
  };

  getLatestPendingJob = () => {
    const jobs = this.getPendingJobsFromStorage();
    if (jobs.length === 0) {
      return null;
    }

    const sortedJobs = jobs.slice().sort((left, right) => {
      const leftAt = new Date(left.updatedAt || left.createdAt || 0).getTime();
      const rightAt = new Date(right.updatedAt || right.createdAt || 0).getTime();
      return rightAt - leftAt;
    });

    return sortedJobs[0];
  };

  normalizeProgressPercent = (progressPercent) => {
    if (progressPercent === null || progressPercent === undefined || progressPercent === '') {
      return null;
    }

    const numericValue = Number(progressPercent);
    if (Number.isNaN(numericValue)) {
      return null;
    }

    return Math.max(0, Math.min(PERCENT_MAX, Math.round(numericValue)));
  };

  wait = (ms) => {
    return new Promise((resolve) => {
      this.pollingTimer = setTimeout(resolve, ms);
    });
  };

  finalizeSuccess = (summary, rows) => {
    this.setState({
      isUploading: false,
      showUploadDialog: false,
      uploadStatus: 'completed',
      progressPercent: PERCENT_MAX,
      resultSummary: summary,
      resultRows: rows,
    });

    this.displayNotification('法人番号AI付与が完了しました。', 'success');
  };

  resumePendingJob = () => {
    const pendingJob = this.getLatestPendingJob();
    if (!pendingJob || !pendingJob.jobId) {
      return Promise.resolve();
    }

    this.clearPollingTimer();
    this.pollingCount = 0;

    this.setState({
      showUploadDialog: true,
      isUploading: true,
      uploadStatus: pendingJob.status || 'queued',
      progressPercent: this.normalizeProgressPercent(pendingJob.progressPercent),
      resultSummary: pendingJob.summary || null,
    });

    return this.pollUntilCompleted(pendingJob.jobId)
      .then(({ summary, rows }) => {
        this.removePendingJob(pendingJob.jobId);
        this.finalizeSuccess(summary, rows);
      })
      .catch((error) => {
        if (error && error.shouldRemovePending) {
          this.removePendingJob(pendingJob.jobId);
        }

        this.setState({
          isUploading: false,
          uploadStatus: 'failed',
          progressPercent: null,
        });
        this.displayNotification(this.extractErrorMessage(error), 'error');
      });
  };

  extractErrorMessage = (error) => {
    if (error && error.message === 'polling_timeout') {
      return '処理に時間がかかっています。しばらく経ってから再実行してください。';
    }

    if (error && error.message === 'job_id_not_found') {
      return 'ジョブの開始に失敗しました。しばらく経ってから再実行してください。';
    }

    if (error && error.code === 'ECONNABORTED') {
      return '処理に時間がかかっています。件数を減らして再実行するか、しばらく経ってからお試しください。';
    }

    const errorData = error && error.response && error.response.data ? error.response.data : null;
    const errorCode =
      (errorData && errorData.error && errorData.error.code) ||
      (errorData && errorData.code);

    if (errorCode === 'TIMEOUT') {
      return '処理に時間がかかっています。件数を減らして再実行するか、しばらく経ってからお試しください。';
    }

    const message = errorData ? errorData.message : null;

    if (typeof message === 'string' && message) {
      return message;
    }

    if (message && typeof message === 'object') {
      const firstKey = Object.keys(message)[0];
      const firstValue = firstKey ? message[firstKey] : null;

      if (Array.isArray(firstValue)) {
        return firstValue.join('\n');
      }

      if (typeof firstValue === 'string') {
        return firstValue;
      }
    }

    return 'アップロードに失敗しました。';
  };

  handleSelectFile = (file, options = {}) => {
    const formData = new FormData();
    formData.append('csv', file);

    Object.keys(options).forEach((key) => {
      const value = options[key];
      if (value !== null && value !== undefined && String(value).trim() !== '') {
        formData.append(key, String(value).trim());
      }
    });

    this.clearPollingTimer();
    this.pollingCount = 0;

    this.setState({
      isUploading: true,
      uploadStatus: 'queued',
      progressPercent: 0,
    });

    let currentJobId = null;

    return this.props.startCorporateNumberAssignAsync(formData)
      .then((response) => {
        const data = response && response.data && response.data.data ? response.data.data : {};
        const jobId = data.job_id;

        if (!jobId) {
          throw new Error('job_id_not_found');
        }

        currentJobId = jobId;

        this.upsertPendingJob({
          jobId,
          status: data.status || 'queued',
          progressPercent: this.normalizeProgressPercent(data.progress_percent),
          summary: data.summary || null,
        });

        this.setState({
          uploadStatus: data.status || 'queued',
          progressPercent: this.normalizeProgressPercent(data.progress_percent),
          resultSummary: data.summary || null,
        });

        return this.pollUntilCompleted(jobId);
      })
      .then(({ summary, rows }) => {
        if (currentJobId) {
          this.removePendingJob(currentJobId);
        }

        this.finalizeSuccess(summary, rows);
      })
      .catch((error) => {
        this.clearPollingTimer();

        if (currentJobId && error && error.shouldRemovePending) {
          this.removePendingJob(currentJobId);
        }

        this.setState({
          isUploading: false,
          uploadStatus: 'failed',
          progressPercent: null,
        });
        this.displayNotification(this.extractErrorMessage(error), 'error');
      });
  };

  pollUntilCompleted = async (jobId) => {
    while (this.pollingCount < MAX_POLLING_COUNT) {
      this.pollingCount += 1;

      const statusResponse = await this.props.getCorporateNumberAssignAsyncStatus(jobId);
      const statusData = statusResponse && statusResponse.data && statusResponse.data.data ? statusResponse.data.data : {};
      const status = statusData.status || 'queued';

      this.upsertPendingJob({
        jobId,
        status,
        progressPercent: this.normalizeProgressPercent(statusData.progress_percent),
        summary: statusData.summary || null,
      });

      this.setState({
        uploadStatus: status,
        progressPercent: this.normalizeProgressPercent(statusData.progress_percent),
        resultSummary: statusData.summary || null,
      });

      if (status === 'failed') {
        try {
          await this.props.getCorporateNumberAssignAsyncResult(jobId);
          const failedError = new Error('job_failed');
          failedError.shouldRemovePending = true;
          throw failedError;
        } catch (error) {
          error.shouldRemovePending = true;
          throw error;
        }
      }

      if (status === 'completed') {
        const resultResponse = await this.props.getCorporateNumberAssignAsyncResult(jobId);
        const resultData = resultResponse && resultResponse.data && resultResponse.data.data ? resultResponse.data.data : {};

        if (resultData.ready === false) {
          await this.wait(POLLING_INTERVAL_MS);
          continue;
        }

        return {
          summary: statusData.summary || resultData.summary || null,
          rows: Array.isArray(resultData.rows) ? resultData.rows : [],
        };
      }

      await this.wait(POLLING_INTERVAL_MS);
    }

    throw new Error('polling_timeout');
  };

  handleDownloadTemplate = () => {
    const templateUrl = (process.env.REACT_APP_CORPORATE_NUMBER_ASSIGN_CSV_TEMPLATE || '').trim();
    const apiTemplatePath = '/corporations/assign-corporate-number/template';
    const isInvalidTemplateUrl =
      !templateUrl ||
      templateUrl === 'undefined' ||
      templateUrl === 'null' ||
      templateUrl.endsWith(apiTemplatePath) ||
      templateUrl.includes(`${apiTemplatePath}?`);

    if (isInvalidTemplateUrl) {
      this.displayNotification('ひな形CSVのURLを指定してください。', 'error');
      return;
    }

    window.open(templateUrl);
  };

  toCsvCell = (value) => {
    if (value === null || value === undefined) {
      return '""';
    }

    const escaped = String(value).replace(/"/g, '""');
    return `"${escaped}"`;
  };

  isDisplayTargetHeader = (header) => {
    return header !== 'AI確定';
  };

  getColumnStyle = (header) => {
    if (header === '法人番号') {
      return { width: '120px' };
    }

    if (header === 'AI自信度') {
      return { width: '70px' };
    }

    return undefined;
  };

  buildCsvText = (rows) => {
    if (!rows || rows.length === 0) {
      return '';
    }

    const headers = Object.keys(rows[0]).filter((header) => this.isDisplayTargetHeader(header));
    if (headers.length === 0) {
      return '';
    }

    const headerLine = headers.map((header) => this.toCsvCell(header)).join(',');
    const bodyLines = rows.map((row) => headers.map((header) => this.toCsvCell(row[header])).join(','));

    return [headerLine, ...bodyLines].join('\n');
  };

  getSummaryCount = (summary, ...keys) => {
    if (!summary) {
      return 0;
    }

    for (let index = 0; index < keys.length; index += 1) {
      const value = summary[keys[index]];
      if (value !== null && value !== undefined && value !== '') {
        const numericValue = Number(value);
        return Number.isNaN(numericValue) ? 0 : numericValue;
      }
    }

    return 0;
  };

  handleDownloadResultCsv = () => {
    const downloadTargetRows = this.getFilteredResultRows(this.state.resultRows);
    if (!downloadTargetRows || downloadTargetRows.length === 0) {
      return;
    }

    const csvText = this.buildCsvText(downloadTargetRows);
    const blob = new Blob([`\uFEFF${csvText}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.setAttribute('download', 'corporate-number-assign-result.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  getRowValueByKey = (row, exactKey) => {
    if (!row || !exactKey) {
      return '';
    }

    if (row[exactKey] !== null && row[exactKey] !== undefined) {
      return String(row[exactKey]);
    }

    const fuzzyKey = Object.keys(row).find((key) => key && key.includes(exactKey));
    if (fuzzyKey && row[fuzzyKey] !== null && row[fuzzyKey] !== undefined) {
      return String(row[fuzzyKey]);
    }

    return '';
  };

  normalizeConfidenceValue = (value) => {
    if (value === null || value === undefined) {
      return null;
    }

    const normalized = String(value).replace(/[%％,\s]/g, '');
    if (!normalized) {
      return null;
    }

    const numeric = Number(normalized);
    if (Number.isNaN(numeric)) {
      return null;
    }

    if (numeric <= 1) {
      return numeric * PERCENT_MAX;
    }

    return numeric;
  };

  isLowConfidenceRow = (row) => {
    const confidenceValue = this.getRowValueByKey(row, 'AI自信度');
    const normalizedConfidence = this.normalizeConfidenceValue(confidenceValue);
    if (normalizedConfidence === null) {
      return false;
    }

    return normalizedConfidence < PERCENT_MAX;
  };

  isUnassignedRow = (row) => {
    const corporateNumber = this.getRowValueByKey(row, '法人番号');
    return String(corporateNumber || '').trim() === '';
  };

  isMatchedSearchKeyword = (row) => {
    const keyword = (this.state.searchKeyword || '').trim().toLowerCase();
    if (!keyword) {
      return true;
    }

    const tokens = keyword.split(/\s+/).filter((token) => token);
    if (tokens.length === 0) {
      return true;
    }

    const targets = [
      this.getRowValueByKey(row, '会社名'),
      this.getRowValueByKey(row, '住所'),
      this.getRowValueByKey(row, '法人番号'),
    ];

    const targetText = targets.join(' ').toLowerCase();

    return tokens.some((token) => targetText.includes(token));
  };

  getFilteredResultRows = (rows) => {
    if (!Array.isArray(rows)) {
      return [];
    }

    return rows.filter((row) => {
      const isMatchedKeyword = this.isMatchedSearchKeyword(row);
      const isMatchedFilter = (() => {
        if (this.state.confidenceFilter === 'lt100') {
          return this.isLowConfidenceRow(row);
        }

        if (this.state.confidenceFilter === 'unassigned') {
          return this.isUnassignedRow(row);
        }

        return true;
      })();

      return isMatchedKeyword && isMatchedFilter;
    });
  };

  handleSearch = () => {
    this.setState({ searchKeyword: this.state.searchInputValue });
  };

  handleSearchFieldKeyDown = (event) => {
    if (event.key === 'Enter') {
      this.handleSearch();
    }
  };

  renderResult = () => {
    const { classes } = this.props;
    const { resultRows, resultSummary } = this.state;

    if (!resultRows || resultRows.length === 0) {
      return null;
    }

    const headers = Object.keys(resultRows[0]).filter((header) => this.isDisplayTargetHeader(header));
    const filteredRows = this.getFilteredResultRows(resultRows);

    if (headers.length === 0) {
      return null;
    }

    return (
      <div className={classes.resultContainer}>
        <div className={classes.resultTitle}>付与結果一覧</div>
        <div className={classes.summaryAndFilterRow}>
          {resultSummary ? (
            <div className={classes.summary}>
              <div className={classes.summaryItem}>総件数: {this.getSummaryCount(resultSummary, 'line_count', 'total_count')}件</div>
              <div className={classes.summaryItem}>付与件数: {this.getSummaryCount(resultSummary, 'assigned_count')}件</div>
              <div className={classes.summaryItem}>スキップ件数: {this.getSummaryCount(resultSummary, 'skipped_count')}件</div>
            </div>
          ) : (
            <div />
          )}

          <div className={classes.resultFilterRow}>
            <div className={classes.searchGroup}>
              <TextField
                id="corporate-number-assign-search"
                type="search"
                placeholder="○○株式会社 ○○県"
                value={this.state.searchInputValue}
                onChange={(event) => this.setState({ searchInputValue: event.target.value })}
                onKeyDown={this.handleSearchFieldKeyDown}
                InputProps={{ disableUnderline: true }}
                className={classes.searchField}
                fullWidth
              />
              <ArrowToolTip text={'入力した文字は空白を区切りとして「会社名 住所 法人番号」の項目をOR検索します。'}>
                <MuiButton className={classes.searchIconButton} onClick={this.handleSearch}>
                  <img src={SearchIcon} alt="検索" />
                </MuiButton>
              </ArrowToolTip>

              <ArrowToolTip text="表示条件絞り込み">
                <div>
                  <DropdownMenu
                    menuItems={[
                      { name: '絞り込みなし', func: () => this.setState({ confidenceFilter: 'all' }) },
                      { name: 'AI自信度100未満', func: () => this.setState({ confidenceFilter: 'lt100' }) },
                      { name: '未付与', func: () => this.setState({ confidenceFilter: 'unassigned' }) },
                    ]}
                    classes={{ btn: classes.iconButton }}
                    popperPlacement="bottom-end"
                    selected={
                      this.state.confidenceFilter === 'lt100'
                        ? 'AI自信度100未満'
                        : this.state.confidenceFilter === 'unassigned'
                          ? '未付与'
                          : '絞り込みなし'
                    }
                  >
                    <SettingIcon color="#71737B" />
                  </DropdownMenu>
                </div>
              </ArrowToolTip>
            </div>
          </div>
        </div>

        <div className={classes.tableWrap}>
          <table className={classes.table}>
            <thead>
              <tr>
                {headers.map((header) => (
                  <th key={header} className={classes.th} style={this.getColumnStyle(header)}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 && (
                <tr>
                  <td className={classes.td} colSpan={headers.length}>
                    <div className={classes.noResultText}>条件に一致するデータはありません。</div>
                  </td>
                </tr>
              )}
              {filteredRows.map((row, index) => (
                <tr key={index}>
                  {headers.map((header) => (
                    <td key={`${header}-${index}`} className={classes.td} style={this.getColumnStyle(header)}>{row[header] !== null && row[header] !== undefined ? String(row[header]) : ''}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Button
          text="付与結果をCSVダウンロード"
          type="default"
          handleClickButton={this.handleDownloadResultCsv}
          style={{ marginTop: '14px' }}
        />
      </div>
    );
  };

  render() {
    const { classes, user } = this.props;

    return (
      <Wrapper className={classes.root}>
        {this.state.showNotification && (
          <div style={{ position: 'fixed', zIndex: 10000, top: 10, width: '98%', left: '1%' }}>
            <NotificationBar
              message={this.state.notificationMessage}
              type={this.state.notificationType}
              open={this.state.showNotification}
              onHandleClose={() => this.setState({ showNotification: false })}
              autoClose={false}
              disableOnclickListener={true}
            />
          </div>
        )}

        <NotificationBanner user={user} />
        <Header title="法人番号AI付与" type="dark" hasBackBtn={false} />

        <div className={classes.main}>
          <div className={classes.container}>
            <Typography variant="display1" className={classes.title}>AIによって法人番号を付与することができます</Typography>
            <Typography variant="body2" className={classes.subheading}>
              会社名・住所を含むCSVをアップロードすると、AIが法人番号を付与します。<br />
              必須ヘッダーは「会社名」です。住所は任意です。<br />
              業種・従業員数・資本金・売上・上場区分など、他の条件でも指定できます。<br />
              処理結果は一覧表示され、CSVでダウンロードできます。<br />
              <b>処理中に他の画面へ遷移しても処理は継続され、この画面に戻ると進捗・結果を確認できます。</b>
            </Typography>
            <Button
              text="CSVをアップロード"
              type="default"
              handleClickButton={this.toggleUploadDialog}
              style={{ marginTop: '52px', paddingLeft: '14px' }}
            />
          </div>

          {this.renderResult()}
        </div>

        <UploadCsvDialog
          isOpen={this.state.showUploadDialog}
          toggleDialog={this.toggleUploadDialog}
          onSelectFile={this.handleSelectFile}
          onDownloadTemplate={this.handleDownloadTemplate}
          onValidationError={(message) => this.displayNotification(message, 'error')}
          isUploading={this.state.isUploading}
          uploadStatus={this.state.uploadStatus}
          progressPercent={this.state.progressPercent}
          uploadSummary={this.state.resultSummary}
        />
      </Wrapper>
    );
  }
}

CorporateNumberAssign.propTypes = {
  classes: PropTypes.object.isRequired,
  user: PropTypes.object,
  getUserInfo: PropTypes.func.isRequired,
  startCorporateNumberAssignAsync: PropTypes.func.isRequired,
  getCorporateNumberAssignAsyncStatus: PropTypes.func.isRequired,
  getCorporateNumberAssignAsyncResult: PropTypes.func.isRequired,
};

export default withStyles(styles)(CorporateNumberAssign);



// WEBPACK FOOTER //
// ./src/components/CorporateNumberAssign/CorporateNumberAssign.jsx